import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import {
  ActivityIndicator,
  Keyboard,
  StyleSheet,
  StyleProp,
  TouchableHighlight,
  View,
  ViewStyle,
} from 'react-native'

import { events, bindingTypes, actionTypes } from '@adalo/constants'

// utils
import { getBindingValue } from 'utils/dependencies'
import { executeAction, actionContextTypes } from 'utils/actions'
import { evaluateCondition } from 'utils/conditions'

// types
import { IAction, IObject, IReduxState } from 'interfaces'

export interface ActionWrapperProps {
  object: IObject
  bindingData?: {}
  bindingComparison?: string
  hitSlop?: number
  style?: StyleProp<ViewStyle>

  getBinding?: () => null
  getParams?: () => object
  dispatch?: () => void
}

export interface ActionWrapperState {
  active?: boolean
}

class ActionWrapper<T extends ActionWrapperProps> extends Component<
  T,
  ActionWrapperState
> {
  static contextTypes = {
    ...actionContextTypes,
  }

  state = {
    active: false,
  }

  getVisible = () => {
    let { object, bindingData, bindingComparison } = this.props
    let binding = object.dataBinding

    if (!binding) {
      return !object.attributes?.hidden
    }

    if (
      binding &&
      binding.source &&
      binding.bindingType === bindingTypes.VISIBILITY
    ) {
      if (bindingData === undefined) {
        return false
      }

      return evaluateCondition(
        bindingData,
        binding.comparator,
        binding.source.dataType,
        bindingComparison
      )
    }

    return !object.attributes?.hidden
  }

  getAffectLayout() {
    let { object } = this.props
    let binding = object.dataBinding

    if (
      binding &&
      binding.options &&
      binding.bindingType === bindingTypes.VISIBILITY
    ) {
      return !!binding.options.affectLayout
    }

    return false
  }

  getLink() {
    let { object } = this.props
    return object.link
  }

  getActions(): object {
    let { object } = this.props
    return object.actions || {}
  }

  hasActions() {
    let actions = this.getActions()

    return Object.keys(actions).length > 0
  }

  handlePress = () => {
    let { getBindings, navigate } = this.context

    let { active } = this.state
    let { dispatch } = this.props

    if (active) {
      return
    }

    let link = this.getLink()
    const actions = this.getActions()

    let actionPromises: Promise<any>[] = []

    this.setState({ active: true })

    let hasLinkAction = false

    Object.keys(actions)
      .filter(id => actions[id].eventType === events.TAP)
      .forEach(id => {
        let linkActions = actions[id].actions.filter((a: IAction) => {
          return a.actionType === actionTypes.NAVIGATE
        })

        if (linkActions.length > 0) {
          hasLinkAction = true
        }

        actionPromises.push(
          executeAction(actions[id], { ...this.context, dispatch })
        )
      })

    if (actionPromises.length > 0) {
      Promise.all(actionPromises).then(() => {
        let { setDirty } = this.context

        setDirty()

        this.setState({ active: false })

        if (hasLinkAction) {
          Keyboard.dismiss()
        }
      })
    }

    if (link) {
      let params = (getBindings && getBindings()) || {}

      navigate({ ...link, params })
    }
  }

  render() {
    let { active } = this.state
    let { children, style, hitSlop = 10 } = this.props
    let visible = this.getVisible()
    let affectLayout = this.getAffectLayout()

    if (!visible) {
      if (affectLayout) {
        return null
      }
    }

    const hasAction = this.hasActions() || this.getLink()
    const opacity = active ? 0.4 : 1

    if (hasAction && visible) {
      return (
        <View style={style}>
          <TouchableHighlight
            underlayColor="transparent"
            activeOpacity={0.7}
            onPress={this.handlePress}
            hitSlop={{
              top: hitSlop,
              bottom: hitSlop,
              left: hitSlop,
              right: hitSlop,
            }}
          >
            <View style={{ flexDirection: 'column' }}>
              <View style={{ flexDirection: 'column', opacity }}>
                {children}
              </View>
              {active ? (
                <View style={styles.loaderWrapper}>
                  <ActivityIndicator />
                </View>
              ) : null}
            </View>
          </TouchableHighlight>
        </View>
      )
    }

    const wrapperOpacity = !visible ? { opacity: 0 } : {}

    return (
      <View
        pointerEvents={visible ? 'auto' : 'none'}
        style={[style, wrapperOpacity]}
      >
        {children}
      </View>
    )
  }
}

const mapStateToProps = (state: IReduxState, props: ActionWrapperProps) => {
  let { object, getBinding, getParams } = props
  let bindingComparison

  if (object.dataBinding && object.dataBinding.comparison) {
    bindingComparison = getBindingValue(object.dataBinding.comparison, {
      state,
      getParams,
      getBinding,
    })
  }

  return {
    bindingComparison,
  }
}

const ConnectedActionWrapper: any = connect(mapStateToProps)(
  ActionWrapper as any
)

export default class WrappedActionWrapper<
  T extends ActionWrapperProps
> extends Component<T> {
  static contextTypes = {
    getBinding: PropTypes.func,
    getParams: PropTypes.func,
  }

  render() {
    const { getBinding, getParams } = this.context

    return (
      <ConnectedActionWrapper
        {...this.props}
        getBinding={getBinding}
        getParams={getParams}
      />
    )
  }
}

const styles = StyleSheet.create({
  loaderWrapper: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
})
